import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

// ── Brand tokens ─────────────────────────────────────────────────────────────
const TEAL      = '#20C997';
const DARK_BLUE = '#003479';

// ── Status meta ──────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'good',        label: 'Good',        activeBg: '#d1fae5', activeColor: '#065f46' },
  { value: 'needs_work',  label: 'Needs Work',  activeBg: '#fef3c7', activeColor: '#92400e' },
  { value: 'not_started', label: 'Not Started', activeBg: '#f3f4f6', activeColor: '#6b7280' },
];

// ── Toast ─────────────────────────────────────────────────────────────────────

const Toast = ({ message, ok }) => (
  <div
    className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl animate-fade-in text-white text-sm font-medium"
    style={{ background: ok ? '#10b981' : '#ef4444' }}
  >
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ok ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'} />
    </svg>
    {message}
  </div>
);

// ── Score Pills 1-10 ─────────────────────────────────────────────────────────

const ScoreSelector = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1">
    {[1,2,3,4,5,6,7,8,9,10].map((n) => {
      const active = value === n;
      return (
        <button
          key={n}
          onClick={() => onChange(active ? null : n)}
          className="w-8 h-8 rounded-lg text-xs font-bold transition-all hover:scale-105"
          style={
            active
              ? { background: DARK_BLUE, color: '#fff' }
              : { background: '#f3f4f6', color: '#9ca3af' }
          }
        >
          {n}
        </button>
      );
    })}
  </div>
);

// ── Subtopic Status Buttons ──────────────────────────────────────────────────

const StatusButtons = ({ value, onChange }) => (
  <div className="flex gap-1">
    {STATUS_OPTIONS.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
          style={
            active
              ? { background: opt.activeBg, color: opt.activeColor }
              : { background: '#f9fafb', color: '#d1d5db' }
          }
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ── Readiness Slider ──────────────────────────────────────────────────────────

const ReadinessSlider = ({ label, value, onChange }) => {
  const pct = value || 0;
  const color = pct >= 70 ? TEAL : pct >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className="text-sm font-bold min-w-[40px] text-right" style={{ color }}>
          {pct}%
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={0}
          max={100}
          value={pct}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{ accentColor: TEAL }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-300">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
    </div>
  );
};

// ── Student summary card (left panel) ────────────────────────────────────────

const StudentCard = ({ student, selected, onClick }) => {
  const avg      = student.avg_topic_score;
  const pct      = student.total_topics > 0
    ? Math.round((student.rated_topics / student.total_topics) * 100)
    : 0;
  const readiness = student.readiness;
  const hasReadiness = readiness && [
    readiness.interview_readiness, readiness.resume_readiness,
    readiness.job_readiness, readiness.communication_skills,
  ].some((v) => v != null && v > 0);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-2xl border transition-all hover:shadow-sm ${
        selected
          ? 'border-[#20C997] shadow-sm ring-1 ring-[#20C997]/30 bg-white'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      {/* Name + avg */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate">{student.student_name}</p>
          <p className="text-xs text-gray-400 truncate">{student.student_email}</p>
        </div>
        {avg != null && (
          <span
            className="flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-lg"
            style={{ background: 'rgba(0,52,121,0.08)', color: DARK_BLUE }}
          >
            {avg.toFixed(1)}/10
          </span>
        )}
      </div>

      {/* Topics rated progress */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: DARK_BLUE }}
          />
        </div>
        <span className="text-xs text-gray-400 flex-shrink-0">
          {student.rated_topics}/{student.total_topics} topics
        </span>
      </div>

      {/* Readiness chips */}
      {hasReadiness && (
        <div className="flex gap-1.5 flex-wrap mt-2">
          {[
            { key: 'interview_readiness', short: 'INT' },
            { key: 'resume_readiness',    short: 'RES' },
            { key: 'job_readiness',       short: 'JOB' },
            { key: 'communication_skills',short: 'COM' },
          ].map(({ key, short }) => {
            const v = readiness?.[key];
            if (v == null) return null;
            const c = v >= 70 ? '#d1fae5' : v >= 40 ? '#fef3c7' : '#fee2e2';
            const t = v >= 70 ? '#065f46' : v >= 40 ? '#92400e' : '#991b1b';
            return (
              <span key={key} className="text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ background: c, color: t }}>
                {short} {v}%
              </span>
            );
          })}
        </div>
      )}
    </button>
  );
};

// ── Scoring form (right panel) ───────────────────────────────────────────────

const ScoringForm = ({
  progress,
  topicScores, setTopicScores,
  subtopicStatuses, setSubtopicStatuses,
  readiness, setReadiness,
  saving, onSave,
}) => {
  const hasTopics = progress.topics?.length > 0;

  return (
    <div className="space-y-4">
      {/* Student header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-white text-sm"
          style={{ background: DARK_BLUE }}
        >
          {progress.student_name.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900">{progress.student_name}</p>
          <p className="text-xs text-gray-500">{progress.course_title} · {progress.batch_name}</p>
        </div>
      </div>

      {/* ── Topic Scores ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Skill Scores</h3>
          <p className="text-xs text-gray-400 mt-0.5">Rate each topic 1–10 and set subtopic statuses</p>
        </div>

        {!hasTopics ? (
          <div className="py-10 flex flex-col items-center justify-center text-gray-400">
            <svg className="w-8 h-8 text-gray-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-sm font-medium text-gray-500">No topics defined for this course</p>
            <p className="text-xs text-gray-400 mt-0.5">Ask an admin to add topics to the course</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {progress.topics.map((topic) => (
              <div key={topic.id} className="px-5 py-4 space-y-3">
                {/* Topic header + score pills */}
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: topicScores[topic.id] != null ? DARK_BLUE : '#d1d5db' }}
                    />
                    <span className="font-semibold text-gray-800 truncate">{topic.title}</span>
                    {topicScores[topic.id] != null && (
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-lg flex-shrink-0"
                        style={{ background: 'rgba(0,52,121,0.08)', color: DARK_BLUE }}
                      >
                        {topicScores[topic.id]}/10
                      </span>
                    )}
                  </div>
                  <ScoreSelector
                    value={topicScores[topic.id]}
                    onChange={(score) =>
                      setTopicScores((prev) => ({ ...prev, [topic.id]: score }))
                    }
                  />
                </div>

                {/* Subtopics */}
                {topic.subtopics?.length > 0 && (
                  <div className="space-y-2 pl-4 border-l-2 border-gray-100">
                    {topic.subtopics.map((sub) => (
                      <div key={sub.id} className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-xs text-gray-500 flex-shrink-0 min-w-[120px]">
                          └ {sub.title}
                        </span>
                        <StatusButtons
                          value={subtopicStatuses[sub.id] || 'not_started'}
                          onChange={(status) =>
                            setSubtopicStatuses((prev) => ({ ...prev, [sub.id]: status }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Readiness Scores ──────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Readiness Scores</h3>
          <p className="text-xs text-gray-400 mt-0.5">Drag each slider to set the readiness percentage</p>
        </div>
        <div className="px-5 py-4 space-y-6">
          {[
            { key: 'interview_readiness', label: 'Interview Readiness' },
            { key: 'resume_readiness',    label: 'Resume Readiness'   },
            { key: 'job_readiness',       label: 'Job Readiness'      },
            { key: 'communication_skills',label: 'Communication Skills'},
          ].map(({ key, label }) => (
            <ReadinessSlider
              key={key}
              label={label}
              value={readiness[key] || 0}
              onChange={(v) => setReadiness((prev) => ({ ...prev, [key]: v }))}
            />
          ))}
        </div>
      </div>

      {/* ── Save ────────────────────────────────────────────────────── */}
      <button
        onClick={onSave}
        disabled={saving}
        className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60 shadow-sm"
        style={{ background: `linear-gradient(135deg,${TEAL},${DARK_BLUE})` }}
      >
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Saving…
          </span>
        ) : (
          'Save All Changes'
        )}
      </button>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const TrainerProgress = () => {
  const { role } = useAuth();
  const [batches, setBatches]                   = useState([]);
  const [selectedBatchId, setSelectedBatchId]   = useState('');
  const [students, setStudents]                 = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [progress, setProgress]                 = useState(null);

  // Form state
  const [topicScores, setTopicScores]           = useState({});
  const [subtopicStatuses, setSubtopicStatuses] = useState({});
  const [readiness, setReadiness]               = useState({
    interview_readiness: 0, resume_readiness: 0,
    job_readiness: 0, communication_skills: 0,
  });

  const [loading, setLoading]                   = useState(true);
  const [studentsLoading, setStudentsLoading]   = useState(false);
  const [progressLoading, setProgressLoading]   = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [toast, setToast]                       = useState(null); // { msg, ok }

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Fetch batches ──────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/api/batches')
      .then((res) => {
        const list = res.data?.batches || res.data || [];
        setBatches(list);
        if (list.length > 0) setSelectedBatchId(list[0].id);
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Fetch students when batch changes ─────────────────────────────────────
  useEffect(() => {
    if (!selectedBatchId) return;
    setStudentsLoading(true);
    setSelectedStudentId('');
    setProgress(null);
    api.get(`/api/progress/batches/${selectedBatchId}/students`)
      .then((res) => setStudents(res.data || []))
      .catch(() => setStudents([]))
      .finally(() => setStudentsLoading(false));
  }, [selectedBatchId]);

  // ── Fetch full progress when student changes ───────────────────────────────
  const loadStudentProgress = useCallback(async (studentId, batchId) => {
    setProgressLoading(true);
    setProgress(null);
    try {
      const res = await api.get(`/api/progress/students/${studentId}/batches/${batchId}`);
      const data = res.data;
      setProgress(data);

      // Initialise form from API data
      const scores   = {};
      const statuses = {};
      for (const topic of (data.topics || [])) {
        scores[topic.id] = topic.score ?? null;
        for (const sub of (topic.subtopics || [])) {
          statuses[sub.id] = sub.status || 'not_started';
        }
      }
      setTopicScores(scores);
      setSubtopicStatuses(statuses);

      const r = data.readiness;
      setReadiness({
        interview_readiness:  r?.interview_readiness  ?? 0,
        resume_readiness:     r?.resume_readiness     ?? 0,
        job_readiness:        r?.job_readiness        ?? 0,
        communication_skills: r?.communication_skills ?? 0,
      });
    } catch {
      showToast('Failed to load student progress', false);
    } finally {
      setProgressLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectStudent = (studentId) => {
    setSelectedStudentId(studentId);
    loadStudentProgress(studentId, selectedBatchId);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedStudentId || !selectedBatchId) return;
    setSaving(true);

    try {
      const scoresList = Object.entries(topicScores)
        .filter(([, v]) => v != null)
        .map(([topic_id, score]) => ({ topic_id, score }));

      const statusesList = Object.entries(subtopicStatuses)
        .map(([subtopic_id, status]) => ({ subtopic_id, status }));

      await Promise.all([
        scoresList.length > 0 &&
          api.put(`/api/progress/students/${selectedStudentId}/batches/${selectedBatchId}/topic-scores`,
            { scores: scoresList }),
        statusesList.length > 0 &&
          api.put(`/api/progress/students/${selectedStudentId}/batches/${selectedBatchId}/subtopic-statuses`,
            { statuses: statusesList }),
        api.put(`/api/progress/students/${selectedStudentId}/batches/${selectedBatchId}/readiness`, readiness),
      ].filter(Boolean));

      showToast('Progress saved successfully!', true);

      // Refresh student list to update summaries
      const res = await api.get(`/api/progress/batches/${selectedBatchId}/students`);
      setStudents(res.data || []);
    } catch {
      showToast('Failed to save progress', false);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="h-8 bg-gray-200 rounded-xl w-52 animate-pulse" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {[1,2,3,4].map(i => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}
            </div>
            <div className="lg:col-span-3 h-96 bg-gray-100 rounded-2xl animate-pulse" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast message={toast.msg} ok={toast.ok} />}

      <div className="space-y-6 animate-fade-in">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(0,52,121,0.08)' }}
            >
              <svg className="w-5 h-5" style={{ color: DARK_BLUE }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Student Progress</h1>
              <p className="text-sm text-gray-500">Rate skills and update readiness for each student</p>
            </div>
          </div>

          {/* Batch selector */}
          {batches.length > 0 && (
            <select
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#20C997]/30 focus:border-[#20C997]"
            >
              {batches.map((b) => (
                <option key={b.id} value={b.id}>{b.batch_name}</option>
              ))}
            </select>
          )}
        </div>

        {batches.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <p className="text-sm font-medium text-gray-500">
                {role === 'admin' ? 'No batches created yet' : 'No batches assigned to you'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {role === 'admin' ? 'Create a batch in Batch Management first' : 'Contact an admin to assign you to a batch'}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* ── Left: Student list ──────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-3">
                Students · {students.length}
              </p>

              {studentsLoading ? (
                <div className="space-y-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : students.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center text-gray-400">
                  <p className="text-sm font-medium text-gray-500">No students enrolled</p>
                </div>
              ) : (
                students.map((s) => (
                  <StudentCard
                    key={s.student_id}
                    student={s}
                    selected={selectedStudentId === s.student_id}
                    onClick={() => handleSelectStudent(s.student_id)}
                  />
                ))
              )}
            </div>

            {/* ── Right: Scoring form ──────────────────────────────────── */}
            <div className="lg:col-span-3">
              {!selectedStudentId ? (
                <div className="bg-white rounded-2xl border border-dashed border-gray-300 flex flex-col items-center justify-center py-24 text-gray-400 h-full">
                  <svg className="w-10 h-10 text-gray-200 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                  </svg>
                  <p className="text-sm font-medium text-gray-500">Select a student to score</p>
                  <p className="text-xs text-gray-400 mt-1">Click any student on the left</p>
                </div>
              ) : progressLoading ? (
                <div className="space-y-4">
                  <div className="h-16 bg-gray-100 rounded-2xl animate-pulse" />
                  <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
                  <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
                </div>
              ) : progress ? (
                <ScoringForm
                  progress={progress}
                  topicScores={topicScores}
                  setTopicScores={setTopicScores}
                  subtopicStatuses={subtopicStatuses}
                  setSubtopicStatuses={setSubtopicStatuses}
                  readiness={readiness}
                  setReadiness={setReadiness}
                  saving={saving}
                  onSave={handleSave}
                />
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 flex items-center justify-center py-20 text-gray-400">
                  <p className="text-sm font-medium">Failed to load student data</p>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrainerProgress;
